/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { Log } from '../../core/logging/Log';
import { ScreenControllerTypes } from '../../core/navigation/ScreenControllerTypes';
import type { TivoPayloadAction } from '../../core/tivo/tivo-redux-wrapper';
import {
    onKeyBackAntiburnOsd,
    onKeyDigitAntiburnOsd,
    onKeyDownAntiburnOsd,
    onKeyGuideAntiburn,
    onKeyLeftAntiburnOsd,
    onKeyOkAntiburnOsd,
    onKeyRightAntiburnOsd,
    onKeyUpAntiburnOsd,
    onMouseClickAntiburnOsd,
    onMouseOverAntiburnOsd,
} from '../antiburnosd/antiburnosd.reducers';
import { onKeyColorBlueCollectionScreen } from '../collectionscreen/collectionscreen.reducers';
import {
    onKeyBackCollectionScreen,
    onKeyChannelDownCollectionScreen,
    onKeyChannelUpCollectionScreen,
    onKeyDigitCollectionScreen,
    onKeyDownCollectionScreen,
    onKeyLeftCollectionScreen,
    onKeyOkCollectionScreen,
    onKeyRightCollectionScreen,
    onKeyUpCollectionScreen,
    onMouseClickCollectionScreen,
    onMouseOutCollectionScreen,
    onMouseOverCollectionScreen,
    onMouseWheelCollectionScreen,
} from '../collectionscreen/navigation/collectionscreennavigation.reducers';
import {
    onKeyOkErrorScreen,
    onKeyOkSos,
    onMouseClickError,
    onMouseOutErrorScreen,
    onMouseOverError,
} from '../error/error.reducers';
import {
    onKeyBackEmptyGuide,
    onKeyDownEmptyGuide,
    onKeyLeftEmptyGuide,
    onKeyOkEmptyGuide,
    onKeyRightEmptyGuide,
    onKeyUpEmptyGuide,
    onLongKeyOkEmptyGuide,
    onMouseClickEmptyGuide,
    onMouseOutEmptyGuide,
    onMouseOverEmptyGuide,
} from '../guide/emptyguide/emptyguide.navigation.reducers';
import {
    onKeyBackGuide,
    onKeyChannelDownGuide,
    onKeyChannelUpGuide,
    onKeyColorBlueGuide,
    onKeyDashGuide,
    onKeyDigitGuide,
    onKeyDownGuide,
    onKeyInfoGuide,
    onKeyLeftGuide,
    onKeyOkGuide,
    onKeyPlayGuide,
    onKeyPlayPauseGuide,
    onKeyRightGuide,
    onKeyUpGuide,
    onLongKeyChannelDownStartGuide,
    onLongKeyChannelUpStartGuide,
    onLongKeyDownStartGuide,
    onLongKeyOkGuide,
    onLongKeyUpStartGuide,
    onMouseClickGuide,
    onMouseOkLongPressStartGuide,
    onMouseOkLongPressStopGuide,
    onMouseOutGuide,
    onMouseOverGuide,
    onMouseWheelGuide,
} from '../guide/guidenavigation/guidenavigation.reducers';
import { stopGuidePageScroll, stopTurboScroll } from '../guide/turboscroll/turboscroll.reducers';
import {
    onKeyBackMyShows,
    onKeyChannelDownMyShows,
    onKeyChannelUpMyShows,
    onKeyDigitMyShows,
    onKeyDownMyShows,
    onKeyLeftMyShows,
    onKeyOkMyShows,
    onKeyPlayMyShows,
    onKeyRightMyShows,
    onKeyUpMyShows,
    onMouseClickMyShows,
    onMouseOutMyShows,
    onMouseOverMyShows,
    onMouseWheelMyShows,
} from '../myshows/myshowsnavigation/myshowsnavigation.reducers';
import {
    onKeyBackProgramScreen,
    onKeyDigitProgramScreen,
    onKeyDownProgramScreen,
    onKeyLeftProgramScreen,
    onKeyOkProgramScreen,
    onKeyRightProgramScreen,
    onKeyUpProgramScreen,
    onMouseClickProgramScreen,
    onMouseOutProgramScreen,
    onMouseOverProgramScreen,
} from '../programscreen/navigation/programscreennavigation.reducers';
import { showPCHelpScreen, showPreviousScreen } from '../screens/screens.reducers';
import {
    onKeyBackAccount,
    onKeyDownAccount,
    onKeyGuideAccount,
    onKeyOkAccount,
    onKeyUpAccount,
    onMouseClickAccount,
    onMouseOutAccount,
    onMouseOverAccount,
} from '../settings/account/accountnavigation.reducers';
import {
    onKeyBackHelp,
    onKeyDownHelp,
    onKeyGuideHelp,
    onKeyOkHelp,
    onKeyUpHelp,
    onMouseClickHelp,
    onMouseOutHelp,
    onMouseOverHelp,
} from '../settings/help/help.reducers';
import {
    onKeyBackPcHelp,
    onKeyDownPcHelp,
    onKeyGuidePcHelp,
    onKeyOkPcHelp,
    onKeyUpPcHelp,
    onMouseClickPcHelp,
    onMouseOutPcHelp,
    onMouseOverPcHelp,
} from '../settings/help/pchelp/pchelp.reducers';
import {
    onKeyBackHideAdultContentSettings,
    onKeyDownHideAdultContentSettings,
    onKeyOkHideAdultContentSettings,
    onKeyUpHideAdultContentSettings,
    onMouseClickHideAdultContentSettings,
    onMouseOutHideAdultContentSettings,
    onMouseOverHideAdultContentSettings,
} from '../settings/hideadultcontent/hideadultcontentnavigation/hideadultcontentnavigation.reducers';
import {
    onKeyBackInfoScreen,
    onKeyGuideInfoScreen,
} from '../settings/info/infonavigation.reducers';
import {
    onKeyBackMainSettings,
    onKeyDigitMainSettings,
    onKeyDownMainSettings,
    onKeyGuideMainSettings,
    onKeyLeftMainSettigs,
    onKeyOkMainSettings,
    onKeyRightMainSettings,
    onKeyUpMainSettings,
    onMouseClickMainSettings,
    onMouseOutMainSettings,
    onMouseOverMainSettings,
} from '../settings/mainsettings/mainsettings.reducers';
import {
    onKeyBackOnePassManager,
    onKeyChannelDownOnePassManager,
    onKeyChannelUpOnePassManager,
    onKeyDigitOnePassManager,
    onKeyDownOnePassManager,
    onKeyGuideOnePassManager,
    onKeyLeftOnePassManager,
    onKeyOkOnePassManager,
    onKeyRightOnePassManager,
    onKeyUpOnePassManager,
    onMouseClickOnePassManager,
    onMouseOutOnePassManager,
    onMouseOverOnePassManager,
    onMouseWheelOnePassManager,
} from '../settings/onepassmanager/onepassmanager.reducers';
import {
    onKeyBackPCSettings,
    onKeyDigitPCSettings,
    onKeyDownPCSettings,
    onKeyLeftPCSettings,
    onKeyOkPCSettings,
    onKeyRightPCSettings,
    onKeyUpPCSettings,
    onMouseClickPCSettings,
    onMouseOutPCSettings,
    onMouseOverPCSettings,
} from '../settings/parentalcontrols/parentalcontrolsnavigation/parentalcontrolsnavigation.reducers';
import {
    onKeyBackSetRatingLimits,
    onKeyDownSetRatingLimits,
    onKeyLeftSetRatingLimits,
    onKeyRightSetRatingLimits,
    onKeyUpSetRatingLimits,
    onMouseClickSetRatingLimits,
    onMouseOutSetRatingLimits,
    onMouseOverSetRatingLimits,
} from '../settings/parentalcontrols/setratinglimits/setratinglimitsnavigation/setratinglimitsnavigation.reducers';
import {
    onKeyBackMyShowsOptionsMenu,
    onKeyDownMyShowsOptionsMenu,
    onKeyOkMyShowsOptionsMenu,
    onKeyUpMyShowsOptionsMenu,
    onMouseOutMyShowsOptions,
    onMouseOverMyShowsOptions,
} from '../settings/userpreferences/myshowsoptionsmenu/myshowsoptionsmenu.navigation';
import {
    onKeyBackOnepassAndRecordingsOptionsMenu,
    onKeyDownOnePassAndRecordingsOptions,
    onKeyLeftOnePassAndRecordingsOptions,
    onKeyRightOnePassAndRecordingsOptions,
    onKeyUpOnePassAndRecordingsOptions,
    onMouseClickOnePassAndRecordingOptions,
    onMouseOutOnePassAndRecordingsOptions,
    onMouseOverOnePassAndRecordingsOptions,
} from '../settings/userpreferences/onepassandrecordings/onepassandrecordings.navigation';
import {
    onKeyBackUserPreferences,
    onKeyDownUserPreferences,
    onKeyGuideUserPreferences,
    onKeyOkUserPreferences,
    onKeyUpUserPreferences,
    onMouseClickUserPreferences,
    onMouseOutUserPreferences,
    onMouseOverUserPreferences,
} from '../settings/userpreferences/userpreferences.reducers';
import {
    onKeyBackMenuLanguage,
    onKeyDownMenuLanguage,
    onKeyGuideMenuLanguage,
    onKeyOkMenuLanguage,
    onKeyUpMenuLanguage,
    onMouseClickMenuLanguage,
    onMouseOutMenuLanguage,
    onMouseOverMenuLanguage,
} from '../settings/userpreferences/userprefmenulanguage/userprefmenulanguage.reducers';
import {
    onKeyBackUserPrefVideoWindowOperations,
    onKeyGuideUserPrefVideoWindowOperations,
    onKeyLeftUserPrefVideoWindowOperations,
    onKeyOkUserPrefVideoWindowOperations,
    onKeyRightUserPrefVideoWindowOperations,
    onMouseClickInteractiveLayerOperations,
    onMouseOutUserPrefVideoWindowOperations,
    onMouseOverUserPrefVideoWindowOperations,
} from '../settings/userpreferences/userprefvideowindow/userprefvideowindow.reducers';
import {
    onKeyBackSidebarNavigationMenu,
    onKeyDownSidebarNavigationMenu,
    onKeyOkSidebarNavigationMenu,
    onKeyRightSidebarNavigationMenu,
    onKeyUpSidebarNavigationMenu,
    onMouseClickSidebarNavigationMenu,
    onMouseOutSidebarNavigationMenu,
    onMouseOverSidebarNavigationMenu,
} from '../sidebarnavigationmenu/sidebarmenunavigation/sidebarmenunavigation.reducers';
import {
    onKeyBackSignIn,
    onKeyDownSignIn,
    onKeyOkSignIn,
    onKeyUpSignIn,
    onMouseClickSignIn,
    onMouseOutSignIn,
    onMouseOverSignIn,
} from '../signin/signinnavigation.reducers';
import { onKeyOkSplashScreen } from '../splashscreen/splashscreen.reducers';
import {
    clearLongPressIntervalVideoPlayerUi,
    onKeyAdvanceVideoPlayerUi,
    onKeyBackVideoPlayerUi,
    onKeyChannelDownVideoPlayerUi,
    onKeyChannelUpVideoPlayerUi,
    onKeyDigitVideoPlayerUi,
    onKeyDownVideoPlayerUi,
    onKeyExitVideoPlayerUi,
    onKeyFastForwardVideoPlayerUi,
    onKeyGuideVideoPlayerUi,
    onKeyInfoVideoPlayerUi,
    onKeyLeftVideoPlayerUi,
    onKeyLongLeftVideoPlayerUi,
    onKeyLongRightVideoPlayerUi,
    onKeyOkVideoPlayerUi,
    onKeyPauseVideoPlayerUi,
    onKeyPlayPauseVideoPlayerUi,
    onKeyPlayVideoPlayerUi,
    onKeyReplayVideoPlayerUi,
    onKeyRewindVideoPlayerUi,
    onKeyRightVideoPlayerUi,
    onKeyStopVideoPlayerUi,
    onKeyTivoVideoPlayerUi,
    onKeyUpVideoPlayerUi,
    onLongKeyChannelDownStartVideoPlayerUi,
    onLongKeyChannelUpStartVideoPlayerUi,
    onLongKeyDownVideoPlayerUi,
    onLongKeyOkVideoPlayerUi,
    onLongKeyStopVideoPlayerUi,
    onLongKeyUpVideoPlayerUi,
    onMouseClickVideoPlayerUi,
    onMouseOkLongPressOneLineGuide,
    onMouseOkLongPressStopOneLineGuide,
    onMouseOutVideoPlayerUi,
    onMouseOverVideoPlayerUi,
    onMouseWheelVideoPlayerUi,
} from '../videoplayerui/videoplayeruinavigation.reducers';
import { CLEAR_KEY_EVENT, ON_KEY_EVENT, ON_MOUSE_EVENT } from './inputfactory.types';

const TAG = 'inputFactory';

const initialState: {
    key: string;
    code: number;
    identity: number;
    eventType: string;
    value: any;
} = {
    key: '',
    code: 0,
    identity: 0,
    eventType: '',
    value: undefined,
};

const inputFactoryReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<typeof initialState>>{},
) => {
    const id = state.identity + 1;
    switch (type) {
        case ON_KEY_EVENT:
            return { ...state, key: payload.key, code: payload.code, identity: id };
        case ON_MOUSE_EVENT:
            return {
                ...state,
                eventType: payload.eventType,
                value: payload.value,
                identity: id,
            };
        case CLEAR_KEY_EVENT:
            return {
                ...state,
                key: '',
                code: 0,
            };
        default:
            return state;
    }
};

export const onKeyRight = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.ANTIBURN_SCREEN:
            dispatch(onKeyRightAntiburnOsd());
            break;

        case ScreenControllerTypes.COLLECTION_SCREEN:
            dispatch(onKeyRightCollectionScreen());
            break;

        case ScreenControllerTypes.EMPTY_GUIDE_SCREEN:
            dispatch(onKeyRightEmptyGuide());
            break;

        case ScreenControllerTypes.GUIDE:
            dispatch(onKeyRightGuide());
            break;

        case ScreenControllerTypes.PC_SCREEN:
            dispatch(onKeyRightPCSettings());
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyRightVideoPlayerUi());
            break;

        case ScreenControllerTypes.ONEPASS_AND_RECORDINGS_MENU_SCREEN:
            dispatch(onKeyRightOnePassAndRecordingsOptions());
            break;

        case ScreenControllerTypes.PC_SET_RATING_LIMITS_SCREEN:
            dispatch(onKeyRightSetRatingLimits());
            break;

        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onKeyRightMyShows());
            break;

        case ScreenControllerTypes.MAIN_SETTINGS_SCREEN:
            dispatch(onKeyRightMainSettings());
            break;

        case ScreenControllerTypes.SIDEBAR_NAVIGATION_MENU_SCREEN:
            dispatch(onKeyRightSidebarNavigationMenu());
            break;
        case ScreenControllerTypes.PROGRAM_SCREEN:
            dispatch(onKeyRightProgramScreen());
            break;

        case ScreenControllerTypes.USER_PREF_VIDEO_WINDOW_SCREEN:
            dispatch(onKeyRightUserPrefVideoWindowOperations());
            break;

        case ScreenControllerTypes.ONEPASS_MANAGER_SCREEN:
            dispatch(onKeyRightOnePassManager());
            break;

        default:
    }
};

export const onLongRightStart = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyLongRightVideoPlayerUi());
            break;

        default:
    }
};

export const onLongRightStop = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(clearLongPressIntervalVideoPlayerUi());
            break;

        default:
    }
};

export const onKeyLeft = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.ANTIBURN_SCREEN:
            dispatch(onKeyLeftAntiburnOsd());
            break;

        case ScreenControllerTypes.COLLECTION_SCREEN:
            dispatch(onKeyLeftCollectionScreen());
            break;

        case ScreenControllerTypes.EMPTY_GUIDE_SCREEN:
            dispatch(onKeyLeftEmptyGuide());
            break;

        case ScreenControllerTypes.GUIDE:
            dispatch(onKeyLeftGuide());
            break;

        case ScreenControllerTypes.PC_SCREEN:
            dispatch(onKeyLeftPCSettings());
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyLeftVideoPlayerUi());
            break;

        case ScreenControllerTypes.ONEPASS_AND_RECORDINGS_MENU_SCREEN:
            dispatch(onKeyLeftOnePassAndRecordingsOptions());
            break;

        case ScreenControllerTypes.PC_SET_RATING_LIMITS_SCREEN:
            dispatch(onKeyLeftSetRatingLimits());
            break;

        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onKeyLeftMyShows());
            break;

        case ScreenControllerTypes.MAIN_SETTINGS_SCREEN:
            dispatch(onKeyLeftMainSettigs());
            break;

        case ScreenControllerTypes.PROGRAM_SCREEN:
            dispatch(onKeyLeftProgramScreen());
            break;

        case ScreenControllerTypes.USER_PREF_VIDEO_WINDOW_SCREEN:
            dispatch(onKeyLeftUserPrefVideoWindowOperations());
            break;

        case ScreenControllerTypes.ONEPASS_MANAGER_SCREEN:
            dispatch(onKeyLeftOnePassManager());
            break;

        default:
    }
};

export const onLongLeftStart = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyLongLeftVideoPlayerUi());
            break;

        default:
    }
};

export const onLongLeftStop = () => (dispatch: Function) => {
    dispatch(onLongRightStop());
};

export const onKeyUp = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.ACCOUNT_SCREEN:
            dispatch(onKeyUpAccount());
            break;

        case ScreenControllerTypes.ANTIBURN_SCREEN:
            dispatch(onKeyUpAntiburnOsd());
            break;

        case ScreenControllerTypes.COLLECTION_SCREEN:
            dispatch(onKeyUpCollectionScreen());
            break;

        case ScreenControllerTypes.EMPTY_GUIDE_SCREEN:
            dispatch(onKeyUpEmptyGuide());
            break;

        case ScreenControllerTypes.GUIDE:
            dispatch(onKeyUpGuide());
            break;

        case ScreenControllerTypes.HELP_SCREEN:
            dispatch(onKeyUpHelp());
            break;

        case ScreenControllerTypes.PC_HELP_SCREEN:
            dispatch(onKeyUpPcHelp());
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyUpVideoPlayerUi());
            break;

        case ScreenControllerTypes.MAIN_SETTINGS_SCREEN:
            dispatch(onKeyUpMainSettings());
            break;

        case ScreenControllerTypes.SIGN_IN_SCREEN:
            dispatch(onKeyUpSignIn());
            break;

        case ScreenControllerTypes.PC_SCREEN:
            dispatch(onKeyUpPCSettings());
            break;

        case ScreenControllerTypes.PROGRAM_SCREEN:
            dispatch(onKeyUpProgramScreen());
            break;

        case ScreenControllerTypes.MENU_LANGUAGE_SCREEN:
            dispatch(onKeyUpMenuLanguage());
            break;

        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onKeyUpMyShows());
            break;

        case ScreenControllerTypes.MY_SHOWS_OPTIONS_MENU_SCREEN:
            dispatch(onKeyUpMyShowsOptionsMenu());
            break;

        case ScreenControllerTypes.ONEPASS_AND_RECORDINGS_MENU_SCREEN:
            dispatch(onKeyUpOnePassAndRecordingsOptions());
            break;

        case ScreenControllerTypes.ONEPASS_MANAGER_SCREEN:
            dispatch(onKeyUpOnePassManager());
            break;

        case ScreenControllerTypes.PC_HIDE_ADULT_CONTENT_SCREEN:
            dispatch(onKeyUpHideAdultContentSettings());
            break;

        case ScreenControllerTypes.PC_SET_RATING_LIMITS_SCREEN:
            dispatch(onKeyUpSetRatingLimits());
            break;

        case ScreenControllerTypes.SIDEBAR_NAVIGATION_MENU_SCREEN:
            dispatch(onKeyUpSidebarNavigationMenu());
            break;
        case ScreenControllerTypes.USER_PREFERENCES_SCREEN:
            dispatch(onKeyUpUserPreferences());
            break;

        default:
    }
};

export const onKeyDown = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.ACCOUNT_SCREEN:
            dispatch(onKeyDownAccount());
            break;

        case ScreenControllerTypes.ANTIBURN_SCREEN:
            dispatch(onKeyDownAntiburnOsd());
            break;

        case ScreenControllerTypes.COLLECTION_SCREEN:
            dispatch(onKeyDownCollectionScreen());
            break;

        case ScreenControllerTypes.EMPTY_GUIDE_SCREEN:
            dispatch(onKeyDownEmptyGuide());
            break;

        case ScreenControllerTypes.GUIDE:
            dispatch(onKeyDownGuide());
            break;

        case ScreenControllerTypes.HELP_SCREEN:
            dispatch(onKeyDownHelp());
            break;

        case ScreenControllerTypes.PC_HELP_SCREEN:
            dispatch(onKeyDownPcHelp());
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyDownVideoPlayerUi());
            break;

        case ScreenControllerTypes.MAIN_SETTINGS_SCREEN:
            dispatch(onKeyDownMainSettings());
            break;

        case ScreenControllerTypes.MENU_LANGUAGE_SCREEN:
            dispatch(onKeyDownMenuLanguage());
            break;

        case ScreenControllerTypes.SIGN_IN_SCREEN:
            dispatch(onKeyDownSignIn());
            break;

        case ScreenControllerTypes.PC_SCREEN:
            dispatch(onKeyDownPCSettings());
            break;

        case ScreenControllerTypes.PROGRAM_SCREEN:
            dispatch(onKeyDownProgramScreen());
            break;

        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onKeyDownMyShows());
            break;

        case ScreenControllerTypes.MY_SHOWS_OPTIONS_MENU_SCREEN:
            dispatch(onKeyDownMyShowsOptionsMenu());
            break;

        case ScreenControllerTypes.ONEPASS_AND_RECORDINGS_MENU_SCREEN:
            dispatch(onKeyDownOnePassAndRecordingsOptions());
            break;

        case ScreenControllerTypes.ONEPASS_MANAGER_SCREEN:
            dispatch(onKeyDownOnePassManager());
            break;

        case ScreenControllerTypes.PC_HIDE_ADULT_CONTENT_SCREEN:
            dispatch(onKeyDownHideAdultContentSettings());
            break;

        case ScreenControllerTypes.PC_SET_RATING_LIMITS_SCREEN:
            dispatch(onKeyDownSetRatingLimits());
            break;

        case ScreenControllerTypes.SIDEBAR_NAVIGATION_MENU_SCREEN:
            dispatch(onKeyDownSidebarNavigationMenu());
            break;
        case ScreenControllerTypes.USER_PREFERENCES_SCREEN:
            dispatch(onKeyDownUserPreferences());
            break;

        default:
    }
};

export const onKeyOk = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.ACCOUNT_SCREEN:
            dispatch(onKeyOkAccount());
            break;

        case ScreenControllerTypes.COLLECTION_SCREEN:
            dispatch(onKeyOkCollectionScreen());
            break;

        case ScreenControllerTypes.EMPTY_GUIDE_SCREEN:
            dispatch(onKeyOkEmptyGuide());
            break;

        case ScreenControllerTypes.GUIDE:
            dispatch(onKeyOkGuide());
            break;

        case ScreenControllerTypes.HELP_SCREEN:
            dispatch(onKeyOkHelp());
            break;

        case ScreenControllerTypes.PC_HELP_SCREEN:
            dispatch(onKeyOkPcHelp());
            break;

        case ScreenControllerTypes.MAIN_SETTINGS_SCREEN:
            dispatch(onKeyOkMainSettings());
            break;

        case ScreenControllerTypes.MENU_LANGUAGE_SCREEN:
            dispatch(onKeyOkMenuLanguage());
            break;

        case ScreenControllerTypes.SIGN_IN_SCREEN:
            dispatch(onKeyOkSignIn());
            break;

        case ScreenControllerTypes.SOS_SCREEN:
            dispatch(onKeyOkSos());
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyOkVideoPlayerUi());
            break;

        case ScreenControllerTypes.ANTIBURN_SCREEN:
            dispatch(onKeyOkAntiburnOsd());
            break;

        case ScreenControllerTypes.ERROR_SCREEN:
            dispatch(onKeyOkErrorScreen());
            break;

        case ScreenControllerTypes.SPLASH_SCREEN:
            dispatch(onKeyOkSplashScreen());
            break;

        case ScreenControllerTypes.PC_SCREEN:
            dispatch(onKeyOkPCSettings());
            break;

        case ScreenControllerTypes.PC_HIDE_ADULT_CONTENT_SCREEN:
            dispatch(onKeyOkHideAdultContentSettings());
            break;

        case ScreenControllerTypes.PROGRAM_SCREEN:
            dispatch(onKeyOkProgramScreen());
            break;

        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onKeyOkMyShows());
            break;

        case ScreenControllerTypes.MY_SHOWS_OPTIONS_MENU_SCREEN:
            dispatch(onKeyOkMyShowsOptionsMenu());
            break;

        case ScreenControllerTypes.ONEPASS_MANAGER_SCREEN:
            dispatch(onKeyOkOnePassManager());
            break;

        case ScreenControllerTypes.SIDEBAR_NAVIGATION_MENU_SCREEN:
            dispatch(onKeyOkSidebarNavigationMenu());
            break;
        case ScreenControllerTypes.USER_PREFERENCES_SCREEN:
            dispatch(onKeyOkUserPreferences());
            break;
        case ScreenControllerTypes.USER_PREF_VIDEO_WINDOW_SCREEN:
            dispatch(onKeyOkUserPrefVideoWindowOperations());
            break;

        default:
    }
};

export const onLongOkStart = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.EMPTY_GUIDE_SCREEN:
            dispatch(onLongKeyOkEmptyGuide());
            break;
        case ScreenControllerTypes.GUIDE:
            dispatch(onLongKeyOkGuide());
            break;
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onLongKeyOkVideoPlayerUi());
            break;
        default:
    }
};

export const onKeyChannelUp = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.COLLECTION_SCREEN:
            dispatch(onKeyChannelUpCollectionScreen());
            break;

        case ScreenControllerTypes.GUIDE:
            dispatch(onKeyChannelUpGuide());
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyChannelUpVideoPlayerUi());
            break;

        case ScreenControllerTypes.ONEPASS_MANAGER_SCREEN:
            dispatch(onKeyChannelUpOnePassManager());
            break;

        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onKeyChannelUpMyShows());
            break;

        default:
    }
};

export const onKeyChannelDown = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.COLLECTION_SCREEN:
            dispatch(onKeyChannelDownCollectionScreen());
            break;

        case ScreenControllerTypes.GUIDE:
            dispatch(onKeyChannelDownGuide());
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyChannelDownVideoPlayerUi());
            break;

        case ScreenControllerTypes.ONEPASS_MANAGER_SCREEN:
            dispatch(onKeyChannelDownOnePassManager());
            break;

        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onKeyChannelDownMyShows());
            break;

        default:
    }
};

export const onKeyExit = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    if (screenController === ScreenControllerTypes.VIDEO_PLAYER) {
        dispatch(onKeyExitVideoPlayerUi());
    }
};

export const onKeyGuide = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.ACCOUNT_SCREEN:
            dispatch(onKeyGuideAccount());
            break;

        case ScreenControllerTypes.GUIDE:
            Log.i(TAG, 'Already in guide screen');
            break;

        case ScreenControllerTypes.HELP_SCREEN:
            dispatch(onKeyGuideHelp());
            break;

        case ScreenControllerTypes.PC_HELP_SCREEN:
            dispatch(onKeyGuidePcHelp());
            break;

        case ScreenControllerTypes.INFO_SCREEN:
            dispatch(onKeyGuideInfoScreen());
            break;

        case ScreenControllerTypes.MAIN_SETTINGS_SCREEN:
            dispatch(onKeyGuideMainSettings());
            break;

        case ScreenControllerTypes.ONEPASS_MANAGER_SCREEN:
            dispatch(onKeyGuideOnePassManager());
            break;

        case ScreenControllerTypes.MENU_LANGUAGE_SCREEN:
            dispatch(onKeyGuideMenuLanguage());
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyGuideVideoPlayerUi());
            break;

        case ScreenControllerTypes.ANTIBURN_SCREEN:
            dispatch(onKeyGuideAntiburn());
            break;

        case ScreenControllerTypes.USER_PREFERENCES_SCREEN:
            dispatch(onKeyGuideUserPreferences());
            break;
        case ScreenControllerTypes.USER_PREF_VIDEO_WINDOW_SCREEN:
            dispatch(onKeyGuideUserPrefVideoWindowOperations());
            break;
        default:
    }
};

export const onKeyInfo = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            dispatch(onKeyInfoGuide());
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyInfoVideoPlayerUi());
            break;

        default:
    }
};

export const onKeyTivo = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyTivoVideoPlayerUi());
            break;

        default:
    }
};

export const onKeyDigit = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.ANTIBURN_SCREEN:
            dispatch(onKeyDigitAntiburnOsd());
            break;
        case ScreenControllerTypes.COLLECTION_SCREEN:
            dispatch(onKeyDigitCollectionScreen());
            break;
        case ScreenControllerTypes.GUIDE:
            dispatch(onKeyDigitGuide());
            break;
        case ScreenControllerTypes.PC_SCREEN:
            dispatch(onKeyDigitPCSettings());
            break;
        case ScreenControllerTypes.PROGRAM_SCREEN:
            dispatch(onKeyDigitProgramScreen());
            break;
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyDigitVideoPlayerUi());
            break;
        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onKeyDigitMyShows());
            break;
        case ScreenControllerTypes.MAIN_SETTINGS_SCREEN:
            dispatch(onKeyDigitMainSettings());
            break;
        case ScreenControllerTypes.ONEPASS_MANAGER_SCREEN:
            dispatch(onKeyDigitOnePassManager());
            break;
        default:
    }
};

export const onKeyDash = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            dispatch(onKeyDashGuide());
            break;
        default:
    }
};

export const onKeyBack = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.ACCOUNT_SCREEN:
            dispatch(onKeyBackAccount());
            break;

        case ScreenControllerTypes.CLOUD_DVR_HELP_SCREEN:
            dispatch(showPreviousScreen());
            break;

        case ScreenControllerTypes.COLLECTION_SCREEN:
            dispatch(onKeyBackCollectionScreen());
            break;

        case ScreenControllerTypes.EMPTY_GUIDE_SCREEN:
            dispatch(onKeyBackEmptyGuide());
            break;

        case ScreenControllerTypes.GUIDE:
            dispatch(onKeyBackGuide());
            break;

        case ScreenControllerTypes.INFO_SCREEN:
            dispatch(onKeyBackInfoScreen());
            break;

        case ScreenControllerTypes.HELP_SCREEN:
            dispatch(onKeyBackHelp());
            break;

        case ScreenControllerTypes.PC_HELP_SCREEN:
            dispatch(onKeyBackPcHelp());
            break;

        case ScreenControllerTypes.PC_HELP_SCREEN_OPTION:
            dispatch(showPCHelpScreen());
            break;

        case ScreenControllerTypes.MAIN_SETTINGS_SCREEN:
            dispatch(onKeyBackMainSettings());
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyBackVideoPlayerUi());
            break;

        case ScreenControllerTypes.ANTIBURN_SCREEN:
            dispatch(onKeyBackAntiburnOsd());
            break;

        case ScreenControllerTypes.SIGN_IN_SCREEN:
            dispatch(onKeyBackSignIn());
            break;

        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onKeyBackMyShows());
            break;

        case ScreenControllerTypes.MY_SHOWS_OPTIONS_MENU_SCREEN:
            dispatch(onKeyBackMyShowsOptionsMenu());
            break;

        case ScreenControllerTypes.PROGRAM_SCREEN:
            dispatch(onKeyBackProgramScreen());
            break;

        case ScreenControllerTypes.ONEPASS_AND_RECORDINGS_MENU_SCREEN:
            dispatch(onKeyBackOnepassAndRecordingsOptionsMenu());
            break;

        case ScreenControllerTypes.MENU_LANGUAGE_SCREEN:
            dispatch(onKeyBackMenuLanguage());
            break;

        case ScreenControllerTypes.ONEPASS_MANAGER_SCREEN:
            dispatch(onKeyBackOnePassManager());
            break;

        case ScreenControllerTypes.PC_SCREEN:
            dispatch(onKeyBackPCSettings());
            break;

        case ScreenControllerTypes.PC_HIDE_ADULT_CONTENT_SCREEN:
            dispatch(onKeyBackHideAdultContentSettings());
            break;

        case ScreenControllerTypes.PC_SET_RATING_LIMITS_SCREEN:
            dispatch(onKeyBackSetRatingLimits());
            break;

        case ScreenControllerTypes.SIDEBAR_NAVIGATION_MENU_SCREEN:
            dispatch(onKeyBackSidebarNavigationMenu());
            break;

        case ScreenControllerTypes.USER_PREF_VIDEO_WINDOW_SCREEN:
            dispatch(onKeyBackUserPrefVideoWindowOperations());
            break;

        case ScreenControllerTypes.USER_PREFERENCES_SCREEN:
            dispatch(onKeyBackUserPreferences());
            break;

        default:
    }
};

export const onKeyPlay = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            dispatch(onKeyPlayGuide());
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyPlayVideoPlayerUi());
            break;

        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onKeyPlayMyShows());
            break;

        default:
    }
};

export const onKeyStop = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyStopVideoPlayerUi());
            break;

        default:
    }
};

export const onKeyFastForward = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyFastForwardVideoPlayerUi());
            break;

        default:
    }
};

export const onKeyRewind = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyRewindVideoPlayerUi());
            break;

        default:
    }
};

export const onKeyReplay = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyReplayVideoPlayerUi());
            break;

        default:
    }
};

export const onKeyColorBlue = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            dispatch(onKeyColorBlueGuide());
            break;

        case ScreenControllerTypes.EMPTY_GUIDE_SCREEN:
            dispatch(onKeyColorBlueGuide());
            break;

        case ScreenControllerTypes.COLLECTION_SCREEN:
            dispatch(onKeyColorBlueCollectionScreen());
            break;

        default:
    }
};

export const onKeyAdvance = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyAdvanceVideoPlayerUi());
            break;

        default:
    }
};

export const onKeyPlayPause = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            dispatch(onKeyPlayPauseGuide());
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyPlayPauseVideoPlayerUi());
            break;

        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onKeyPlayMyShows());
            break;

        default:
    }
};

export const onKeyPause = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onKeyPauseVideoPlayerUi());
            break;

        default:
    }
};

export const onLongStartDown = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            dispatch(onLongKeyDownStartGuide());
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onLongKeyDownVideoPlayerUi());
            break;

        default:
    }
    Log.i(TAG, 'onLongStartDown');
};

export const onLongStartUp = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            dispatch(onLongKeyUpStartGuide());
            break;

        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onLongKeyUpVideoPlayerUi());
            break;

        default:
    }
    Log.i(TAG, 'onLongStartUp');
};

export const onLongStopDown = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            dispatch(stopTurboScroll());
            break;
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onLongKeyStopVideoPlayerUi());
            break;
        default:
    }
};

export const onLongStopUp = () => (dispatch: Function) => {
    dispatch(onLongStopDown());
};

export const onLongChannelUpStart = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            dispatch(onLongKeyChannelUpStartGuide());
            break;
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onLongKeyChannelUpStartVideoPlayerUi());
            break;

        default:
    }
    Log.i(TAG, 'onLongChannelUpStart');
};

export const onLongChannelDownStart = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            dispatch(onLongKeyChannelDownStartGuide());
            break;
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onLongKeyChannelDownStartVideoPlayerUi());
            break;

        default:
    }
    Log.i(TAG, 'onLongChanneldownStart');
};

export const onLongChannelUpStop = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            dispatch(stopGuidePageScroll());
            break;
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onLongKeyStopVideoPlayerUi());
            break;

        default:
    }
};

export const onLongChannelDownStop = () => (dispatch: Function) => {
    dispatch(onLongChannelUpStop());
};

export const onMouseClick = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.ACCOUNT_SCREEN:
            dispatch(onMouseClickAccount());
            break;

        case ScreenControllerTypes.ANTIBURN_SCREEN:
            dispatch(onMouseClickAntiburnOsd());
            break;

        case ScreenControllerTypes.COLLECTION_SCREEN:
            dispatch(onMouseClickCollectionScreen());
            break;
        case ScreenControllerTypes.EMPTY_GUIDE_SCREEN:
            dispatch(onMouseClickEmptyGuide());
            break;
        case ScreenControllerTypes.ERROR_SCREEN:
            dispatch(onMouseClickError());
            break;
        case ScreenControllerTypes.GUIDE:
            dispatch(onMouseClickGuide());
            break;
        case ScreenControllerTypes.HELP_SCREEN:
            dispatch(onMouseClickHelp());
            break;
        case ScreenControllerTypes.PC_HELP_SCREEN:
            dispatch(onMouseClickPcHelp());
            break;
        case ScreenControllerTypes.MAIN_SETTINGS_SCREEN:
            dispatch(onMouseClickMainSettings());
            break;
        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onMouseClickMyShows());
            break;
        case ScreenControllerTypes.MENU_LANGUAGE_SCREEN:
            dispatch(onMouseClickMenuLanguage());
            break;
        case ScreenControllerTypes.ONEPASS_MANAGER_SCREEN:
            dispatch(onMouseClickOnePassManager());
            break;
        case ScreenControllerTypes.SIGN_IN_SCREEN:
            dispatch(onMouseClickSignIn());
            break;
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onMouseClickVideoPlayerUi());
            break;
        case ScreenControllerTypes.PC_HIDE_ADULT_CONTENT_SCREEN:
            dispatch(onMouseClickHideAdultContentSettings());
            break;
        case ScreenControllerTypes.PC_SCREEN:
            dispatch(onMouseClickPCSettings());
            break;
        case ScreenControllerTypes.PC_SET_RATING_LIMITS_SCREEN:
            dispatch(onMouseClickSetRatingLimits());
            break;
        case ScreenControllerTypes.SIDEBAR_NAVIGATION_MENU_SCREEN:
            dispatch(onMouseClickSidebarNavigationMenu());
            break;
        case ScreenControllerTypes.USER_PREFERENCES_SCREEN:
            dispatch(onMouseClickUserPreferences());
            break;
        case ScreenControllerTypes.MY_SHOWS_OPTIONS_MENU_SCREEN:
            dispatch(onKeyOkMyShowsOptionsMenu());
            break;
        case ScreenControllerTypes.ONEPASS_AND_RECORDINGS_MENU_SCREEN:
            dispatch(onMouseClickOnePassAndRecordingOptions());
            break;
        case ScreenControllerTypes.USER_PREF_VIDEO_WINDOW_SCREEN:
            dispatch(onMouseClickInteractiveLayerOperations());
            break;
        case ScreenControllerTypes.PROGRAM_SCREEN:
            dispatch(onMouseClickProgramScreen());
            break;
        default:
    }
};

export const onMouseEnter = () => (dispatch: Function, getState: Function) => {
    Log.i(TAG, 'onMouseEnter');
};

export const onMouseLeave = () => (dispatch: Function, getState: Function) => {
    Log.i(TAG, 'onMouseLeave');
};

export const onMouseOkLongPressStart = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;
    Log.i(TAG, 'onMouseOkLongPressStart');

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            dispatch(onMouseOkLongPressStartGuide());
            break;
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onMouseOkLongPressOneLineGuide());
            break;
        default:
    }
};

export const onMouseOkLongPressStop = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            dispatch(onMouseOkLongPressStopGuide());
            Log.i(TAG, 'onMouseOkLongPressStop');
            break;
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onMouseOkLongPressStopOneLineGuide());
            break;
        default:
    }
};

export const onMouseOut = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.ACCOUNT_SCREEN:
            dispatch(onMouseOutAccount());
            break;
        case ScreenControllerTypes.COLLECTION_SCREEN:
            dispatch(onMouseOutCollectionScreen());
            break;
        case ScreenControllerTypes.EMPTY_GUIDE_SCREEN:
            dispatch(onMouseOutEmptyGuide());
            break;
        case ScreenControllerTypes.ERROR_SCREEN:
            dispatch(onMouseOutErrorScreen());
            break;
        case ScreenControllerTypes.GUIDE:
            dispatch(onMouseOutGuide());
            break;
        case ScreenControllerTypes.HELP_SCREEN:
            dispatch(onMouseOutHelp());
            break;
        case ScreenControllerTypes.PC_HELP_SCREEN:
            dispatch(onMouseOutPcHelp());
            break;
        case ScreenControllerTypes.MAIN_SETTINGS_SCREEN:
            dispatch(onMouseOutMainSettings());
            break;
        case ScreenControllerTypes.MENU_LANGUAGE_SCREEN:
            dispatch(onMouseOutMenuLanguage());
            break;
        case ScreenControllerTypes.SIGN_IN_SCREEN:
            dispatch(onMouseOutSignIn());
            break;
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onMouseOutVideoPlayerUi());
            break;
        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onMouseOutMyShows());
            break;
        case ScreenControllerTypes.ONEPASS_MANAGER_SCREEN:
            dispatch(onMouseOutOnePassManager());
            break;
        case ScreenControllerTypes.PC_HIDE_ADULT_CONTENT_SCREEN:
            dispatch(onMouseOutHideAdultContentSettings());
            break;
        case ScreenControllerTypes.PC_SCREEN:
            dispatch(onMouseOutPCSettings());
            break;
        case ScreenControllerTypes.PC_SET_RATING_LIMITS_SCREEN:
            dispatch(onMouseOutSetRatingLimits());
            break;
        case ScreenControllerTypes.SIDEBAR_NAVIGATION_MENU_SCREEN:
            dispatch(onMouseOutSidebarNavigationMenu());
            break;
        case ScreenControllerTypes.USER_PREFERENCES_SCREEN:
            dispatch(onMouseOutUserPreferences());
            break;
        case ScreenControllerTypes.MY_SHOWS_OPTIONS_MENU_SCREEN:
            dispatch(onMouseOutMyShowsOptions());
            break;
        case ScreenControllerTypes.ONEPASS_AND_RECORDINGS_MENU_SCREEN:
            dispatch(onMouseOutOnePassAndRecordingsOptions());
            break;
        case ScreenControllerTypes.USER_PREF_VIDEO_WINDOW_SCREEN:
            dispatch(onMouseOutUserPrefVideoWindowOperations());
            break;
        case ScreenControllerTypes.PROGRAM_SCREEN:
            dispatch(onMouseOutProgramScreen());
            break;
        default:
    }
};

export const onMouseOver = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.ACCOUNT_SCREEN:
            dispatch(onMouseOverAccount());
            break;
        case ScreenControllerTypes.EMPTY_GUIDE_SCREEN:
            dispatch(onMouseOverEmptyGuide());
            break;
        case ScreenControllerTypes.ANTIBURN_SCREEN:
            dispatch(onMouseOverAntiburnOsd());
            break;
        case ScreenControllerTypes.COLLECTION_SCREEN:
            dispatch(onMouseOverCollectionScreen());
            break;
        case ScreenControllerTypes.ERROR_SCREEN:
            dispatch(onMouseOverError());
            break;
        case ScreenControllerTypes.GUIDE:
            dispatch(onMouseOverGuide());
            break;
        case ScreenControllerTypes.HELP_SCREEN:
            dispatch(onMouseOverHelp());
            break;
        case ScreenControllerTypes.PC_HELP_SCREEN:
            dispatch(onMouseOverPcHelp());
            break;
        case ScreenControllerTypes.MAIN_SETTINGS_SCREEN:
            dispatch(onMouseOverMainSettings());
            break;
        case ScreenControllerTypes.MENU_LANGUAGE_SCREEN:
            dispatch(onMouseOverMenuLanguage());
            break;
        case ScreenControllerTypes.SIGN_IN_SCREEN:
            dispatch(onMouseOverSignIn());
            break;
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onMouseOverVideoPlayerUi());
            break;
        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onMouseOverMyShows());
            break;
        case ScreenControllerTypes.ONEPASS_MANAGER_SCREEN:
            dispatch(onMouseOverOnePassManager());
            break;
        case ScreenControllerTypes.PC_HIDE_ADULT_CONTENT_SCREEN:
            dispatch(onMouseOverHideAdultContentSettings());
            break;
        case ScreenControllerTypes.PC_SCREEN:
            dispatch(onMouseOverPCSettings());
            break;
        case ScreenControllerTypes.PC_SET_RATING_LIMITS_SCREEN:
            dispatch(onMouseOverSetRatingLimits());
            break;
        case ScreenControllerTypes.SIDEBAR_NAVIGATION_MENU_SCREEN:
            dispatch(onMouseOverSidebarNavigationMenu());
            break;
        case ScreenControllerTypes.USER_PREFERENCES_SCREEN:
            dispatch(onMouseOverUserPreferences());
            break;
        case ScreenControllerTypes.MY_SHOWS_OPTIONS_MENU_SCREEN:
            dispatch(onMouseOverMyShowsOptions());
            break;
        case ScreenControllerTypes.ONEPASS_AND_RECORDINGS_MENU_SCREEN:
            dispatch(onMouseOverOnePassAndRecordingsOptions());
            break;
        case ScreenControllerTypes.USER_PREF_VIDEO_WINDOW_SCREEN:
            dispatch(onMouseOverUserPrefVideoWindowOperations());
            break;
        case ScreenControllerTypes.PROGRAM_SCREEN:
            dispatch(onMouseOverProgramScreen());
            break;
        default:
    }
};

export const onMouseWheel = (wheelDeltaY: number) => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;

    switch (screenController) {
        case ScreenControllerTypes.GUIDE:
            dispatch(onMouseWheelGuide(wheelDeltaY));
            break;
        case ScreenControllerTypes.VIDEO_PLAYER:
            dispatch(onMouseWheelVideoPlayerUi(wheelDeltaY));
            break;
        case ScreenControllerTypes.ONEPASS_MANAGER_SCREEN:
            dispatch(onMouseWheelOnePassManager(wheelDeltaY));
            break;
        case ScreenControllerTypes.MY_SHOWS_SCREEN:
            dispatch(onMouseWheelMyShows(wheelDeltaY));
            break;
        case ScreenControllerTypes.COLLECTION_SCREEN:
            dispatch(onMouseWheelCollectionScreen(wheelDeltaY));
            break;
        default:
    }
};

export default inputFactoryReducer;
